import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganisation } from '../contexts/OrganisationContext';
import { Group, GroupCreate } from '../types/api';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../constants/messages';
import OrganisationTabs from '../components/Organisation/OrganisationTabs';
import { UserGroupIcon, PlusIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Groupes d'une organisation (plan B) : un groupe recoit un role sur des projets,
// ses membres en heritent. Tout membre de l'orga voit la liste ; OWNER/ADMIN creent.
const OrganisationGroupsPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const { currentOrganisation, selectOrganisation, canManageMembers } = useOrganisation();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<GroupCreate>({ nom: '', description: '' });

  useEffect(() => {
    if (orgId && (!currentOrganisation || currentOrganisation.id !== orgId)) {
      selectOrganisation(orgId).catch(() => {
        toast.error(t('groups.loadOrgError'));
        navigate('/organisations');
      });
    }
  }, [orgId, currentOrganisation, selectOrganisation, navigate]);

  const loadGroups = useCallback(async () => {
    if (!currentOrganisation) return;
    try {
      setLoading(true);
      setGroups(await apiService.getOrganisationGroups(currentOrganisation.id));
    } catch (error: unknown) {
      toast.error(t('groups.loadError'));
    } finally {
      setLoading(false);
    }
  }, [currentOrganisation]);

  useEffect(() => {
    if (currentOrganisation) loadGroups();
  }, [currentOrganisation, loadGroups]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageMembers()) {
      toast.error(t('groups.permissions.noManage'));
      return;
    }
    const nom = form.nom.trim();
    if (!nom) {
      toast.error(t('groups.createModal.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      const created = await apiService.createOrganisationGroup(currentOrganisation!.id, {
        nom,
        description: form.description?.trim() || null,
      });
      toast.success(t('groups.createModal.success'));
      setShowCreateModal(false);
      setForm({ nom: '', description: '' });
      navigate(`/organisations/${currentOrganisation!.id}/groups/${created.id}`);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast.error(status === 409 ? t('groups.createModal.nameTaken') : getApiErrorMessage(error, t('groups.createModal.error')));
    } finally {
      setCreating(false);
    }
  };

  if (loading || !currentOrganisation) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('groups.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/organisations/${orgId}/dashboard`)}
            className="text-sm text-accent-400 hover:text-accent-300 mb-2"
          >
            ← {t('groups.backToDashboard')}
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">{currentOrganisation.nom}</h1>
              <p className="mt-2 text-text-secondary">{t('groups.subtitle')}</p>
            </div>
            {canManageMembers() && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('groups.createButton')}
              </button>
            )}
          </div>
        </div>

        <OrganisationTabs orgId={currentOrganisation.id} />

        <div className="bg-surface-2 shadow-card rounded-card overflow-hidden">
          <div className="divide-y divide-border-1">
            {groups.map((group) => (
              <button
                key={group.id}
                onClick={() => navigate(`/organisations/${orgId}/groups/${group.id}`)}
                className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-surface-3"
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500/12 flex items-center justify-center">
                    <UserGroupIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary flex items-center">
                      {group.nom}
                      {group.source === 'entra' && (
                        <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-purple-500/12 text-purple-400">
                          <ArrowPathIcon className="h-3 w-3 mr-1" />
                          {t('groups.source.entra')}
                        </span>
                      )}
                    </p>
                    {group.description && (
                      <p className="text-sm text-text-tertiary truncate">{group.description}</p>
                    )}
                  </div>
                </div>
                <div className="text-xs text-text-tertiary whitespace-nowrap ml-4">
                  {t('groups.members', { count: group.member_count })} · {t('groups.projects', { count: group.project_count })}
                </div>
              </button>
            ))}

            {groups.length === 0 && (
              <div className="px-6 py-12 text-center">
                <UserGroupIcon className="mx-auto h-12 w-12 text-text-quaternary" />
                <p className="mt-2 text-sm font-medium text-text-secondary">{t('groups.empty')}</p>
                <p className="mt-1 text-sm text-text-tertiary">{t('groups.emptyHint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setShowCreateModal(false)}></div>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="bg-surface-2 rounded-md text-text-quaternary hover:text-text-tertiary focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">{t('groups.createModal.title')}</h3>

              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label htmlFor="group-name" className="block text-sm font-medium text-text-secondary">
                    {t('groups.createModal.nameLabel')}
                  </label>
                  <input
                    type="text"
                    id="group-name"
                    value={form.nom}
                    maxLength={100}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                    placeholder={t('groups.createModal.namePlaceholder') as string}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="group-description" className="block text-sm font-medium text-text-secondary">
                    {t('groups.createModal.descriptionLabel')}
                  </label>
                  <textarea
                    id="group-description"
                    value={form.description ?? ''}
                    maxLength={500}
                    rows={2}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  />
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={creating}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {creating ? t('groups.createModal.creating') : t('groups.createModal.create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    {t('cancel', { ns: 'common' })}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganisationGroupsPage;
